import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  PlusCircle,
  DollarSign,
  CreditCard,
  Banknote,
  Wallet,
  Trash2,
} from "lucide-react";
import { numberToMoney } from "@/util/moneyConversion";
import {
  PaymentsOutput,
  PaymentDetailsMethod,
  orderPaymentDetailsMethodTranslations,
} from "@/interfaces/payments";
import {
  OrderPaymentStatus,
  orderPaymentStatusTranslation,
} from "@/interfaces";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import CustomCombobox from "@/components/CustomCombobox";
import CurrencyInput from "@/components/CurrencyInput";
import { DatePickerComponent } from "@/components/DatePickerComponent";

interface OrderPaymentsProps {
  orderId: string;
  totalAmount: number;
  payments: PaymentsOutput[];
  paymentStatus: OrderPaymentStatus;
  onAddPayment: (
    payment: Omit<PaymentsOutput, "id" | "createdAt" | "updatedAt">
  ) => void;
  onMarkAsPaid: (method: PaymentDetailsMethod, amount: number) => void;
  onDeletePayment: (paymentId: string) => void;
}

export function OrderPayments({
  orderId,
  totalAmount,
  payments,
  paymentStatus,
  onAddPayment,
  onMarkAsPaid,
  onDeletePayment,
}: OrderPaymentsProps) {
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  const [newPayment, setNewPayment] = useState<Partial<PaymentsOutput>>({
    orderId,
    method: PaymentDetailsMethod.CASH,
    amount: 0,
    paymentDate: new Date(),
  });
  const [selectedMethod, setSelectedMethod] = useState<
    PaymentDetailsMethod | ""
  >("");

  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const remainingAmount = Math.max(0, totalAmount - totalPaid);

  const handleAddPayment = () => {
    if (newPayment.amount && newPayment.amount > 0 && newPayment.method) {
      onAddPayment(
        newPayment as Omit<PaymentsOutput, "id" | "createdAt" | "updatedAt">
      );
      setIsAddPaymentOpen(false);
      setNewPayment({
        orderId,
        method: PaymentDetailsMethod.CASH,
        amount: 0,
        paymentDate: new Date(),
      });
    }
  };

  const handleMarkAsPaid = () => {
    if (selectedMethod) {
      onMarkAsPaid(selectedMethod as PaymentDetailsMethod, remainingAmount);
      setSelectedMethod("");
    }
  };

  const paymentMethodOptions = Object.entries(
    orderPaymentDetailsMethodTranslations
  ).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  const getPaymentMethodIcon = (method: PaymentDetailsMethod) => {
    switch (method) {
      case PaymentDetailsMethod.CREDIT_CARD:
        return <CreditCard className="w-4 h-4" />;
      case PaymentDetailsMethod.CASH:
        return <Banknote className="w-4 h-4" />;
      default:
        return <Wallet className="w-4 h-4" />;
    }
  };

  return (
    <>
      <Separator className="my-4" />
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" />
            Pagamentos
          </h3>
          <Badge>{orderPaymentStatusTranslation[paymentStatus]}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CustomCombobox
            value={selectedMethod}
            onChange={(value) =>
              setSelectedMethod(value as PaymentDetailsMethod)
            }
            options={paymentMethodOptions}
            placeholder="Método"
            className="w-[120px]"
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleMarkAsPaid}
                  disabled={!selectedMethod || remainingAmount <= 0}
                  size="sm"
                >
                  Pagar Restante
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Pagar o valor restante do pedido: {numberToMoney(remainingAmount)}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Dialog open={isAddPaymentOpen} onOpenChange={setIsAddPaymentOpen}>
            <DialogTrigger asChild>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddPaymentOpen(true)}
                    >
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Parcial
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Adicionar pagamento parcial</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Adicionar Pagamento Parcial</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="amount" className="text-right">
                    Valor
                  </Label>
                  <CurrencyInput
                    id="amount"
                    value={newPayment.amount || ""}
                    onValueChange={(value) =>
                      setNewPayment({
                        ...newPayment,
                        amount: value,
                      })
                    }
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="method" className="text-right">
                    Método
                  </Label>
                  <CustomCombobox
                    value={newPayment.method}
                    onChange={(value) =>
                      setNewPayment({
                        ...newPayment,
                        method: value as PaymentDetailsMethod,
                      })
                    }
                    options={paymentMethodOptions}
                    className="col-span-3 max-w-[150px]"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="method" className="text-right">
                    Data
                  </Label>
                  <DatePickerComponent
                    id="paymentDate"
                    selected={newPayment.paymentDate ?? new Date()}
                    onSelect={(value) =>
                      setNewPayment({
                        ...newPayment,
                        paymentDate: value,
                      })
                    }
                  />
                </div>
              </div>
              <Button onClick={handleAddPayment}>Adicionar Pagamento</Button>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-2">
          {payments.map((payment) => (
            <div
              key={payment.id}
              className="flex items-center justify-between p-2 border rounded"
            >
              <div className="flex items-center gap-2">
                {getPaymentMethodIcon(payment.method)}
                <div>
                  <p className="font-semibold">
                    {orderPaymentDetailsMethodTranslations[payment.method]}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(payment.paymentDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <p className="font-semibold">{numberToMoney(payment.amount)}</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeletePayment(payment.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Excluir este pagamento</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-start gap-5 items-center text-sm">
          <p>
            Total Pago:{" "}
            <span className="font-semibold">{numberToMoney(totalPaid)}</span>
          </p>
          <p>
            Restante:{" "}
            <span className="font-semibold text-orange-500">
              {numberToMoney(remainingAmount)}
            </span>
          </p>
        </div>
      </div>
    </>
  );
}
